import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormArray,
  AbstractControl,
  UntypedFormControl,
} from '@angular/forms';

// ANIMATIONS

// CONFIGS

// COMPONENT
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  fadeOutBackgroundAnimation,
  slideInAnimation,
} from '../../../shared/animations/animation';

// INTERFACES
import { TableAnimateRowDirective } from '../../../shared/directives/table-animate-row.directive';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';

// UTILS
import { trackByControlValue, trackById } from '../../../shared/util';
import { ERfqStatus } from '../../interfaces/rfq.interface';

import { ELineItemRowType } from './../../../invoice/interfaces/invoice.interface';
import { DialogConfig } from './../../../shared/configs/dialog.config';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { UiMessageDialogComponent } from './../../../ui/components/ui-message-dialog/ui-message-dialog.component';

@Component({
  selector: 'lum-rfq-table-form',
  templateUrl: './rfq-table-form.component.html',
  styleUrls: ['./rfq-table-form.component.scss'],
  animations: [fadeOutBackgroundAnimation, slideInAnimation],
})
export class RfqTableFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  form :UntypedFormGroup;

  @Input()
  searchableFields :string[] = [];

  @Input()
  searchStr :string = '';

  @Input()
  rfqs :any[] = []; // TODO: Create interface

  @Input()
  loadingRfqs :boolean = false;

  @Input()
  fromEnterEvent :boolean = false;

  @Input()
  fromTransferOrder :boolean = false;

  @Input()
  fromInvoice :boolean = false;

  @Input()
  isSalesOrderShippedStatus :boolean = false;

  @Input()
  isInvoiceStatusComplete :boolean = false;

  @Input()
  columns :IColumnConfig[] = [];

  @Output()
  removeLineItem = new EventEmitter<number>();

  @Output()
  addRowItems = new EventEmitter<number>();

  @Output()
  addRowItemSubTotal = new EventEmitter<number | null>();

  @Output()
  searchRfq = new EventEmitter<any>(); // TODO: Create interface

  @Output()
  enterRfqEvent = new EventEmitter<{
    item :AbstractControl; // TODO: Create interface
    index :number;
    searchStr :string;
  }>();

  @Output()
  selectRfq = new EventEmitter<{
    item :any; // TODO: Create interface
    tempId :number;
  }>();

  @Output()
  scrollPaginate = new EventEmitter<boolean>();

  @Output()
  recalculateSubtotals = new EventEmitter();

  @ViewChildren('inputRef')
  inputRefs! :QueryList<ElementRef>;

  @ViewChild(MatAutocompleteTrigger)
  autoCompleteTrigger! :MatAutocompleteTrigger;

  @ViewChildren(TableAnimateRowDirective)
  rows :QueryList<TableAnimateRowDirective>;

  isDragged :boolean = false;
  selectAll :UntypedFormControl = new UntypedFormControl(false);
  inputsNodeList :NodeListOf<Element>;

  querySelectorStr :string =
    '.lum-table input.table__search-skus-input:not([type="checkbox"]):not([disabled]):not([readonly])';

  subTotalIndex :number | null = null;
  sortState = { column: '', direction: 'asc' };
  duplicateForm :UntypedFormGroup;
  eLineItemRowType = ELineItemRowType;

  trackByControlValue = trackByControlValue;
  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(private dialog :MatDialog) {}

  ngOnInit() :void {
    this.listentToDOMNodeInsertedEvent();
    this.duplicateForm = this.form;
  }

  ngOnChanges({ form } :SimpleChanges) :void {
    if (form) {
      this.duplicateForm = this.form;
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  drop(event :CdkDragDrop<string[]>) {
    const { previousIndex, currentIndex } = event;
    const control = this.lineItemsFormArray.at(previousIndex);
    const controlCurrent = this.lineItemsFormArray.at(currentIndex);

    if (
      (previousIndex === 0 &&
        controlCurrent.value?.row_type === ELineItemRowType.SUB_TOTAL) ||
      control.value?.row_type === ELineItemRowType.SUB_TOTAL
    ) {
      this.dialog.open(UiMessageDialogComponent, {
        data: {
          title: 'Invalid Action',
          message:
            'Row subtotal cannot be placed on top or next to another subtotal row.',
        },
        width: DialogConfig.sizes.small,
      });

      return;
    }

    this.lineItemsFormArray.removeAt(previousIndex);
    this.lineItemsFormArray.insert(currentIndex, control);
    this.recalculateSubtotals.emit();
  }

  onSelectAll() :void {
    const selectedValue = this.selectAll.value;
    this.lineItemsFormArray.controls.forEach((control) =>
      control.get('selected')?.setValue(!selectedValue),
    );
  }

  onSelectSku(event :MatAutocompleteSelectedEvent, itemId :number) :void {
    const selectedSku = event.option.value.sku;

    // Find if the SKU already exists in the invoice
    const existingIndex = this.lineItemsFormArray.controls.findIndex(
      (control) => control.get('sku')?.value === selectedSku,
    );

    if (existingIndex !== -1 && this.fromInvoice) {
      // If the SKU exists, focus on it instead of adding a duplicate
      this.focusOnExistingItem(existingIndex);
    } else {
      // If not, add the new item and then focus on it
      this.selectRfq.emit({
        item: event.option.value,
        tempId: itemId,
      });
      const targetIndex = Array.from(this.inputRefsNativeElements).findIndex(
        (input :Element, index :number, obj :Element[]) => {
          // Check if input id matches the itemId
          return input.id === `id-${itemId}`;
        },
      );
      this.focusToNextInput({ index: targetIndex });
    }
  }

  focusOnExistingItem(index :number) :void {
    setTimeout(() => {
      const input = this.inputRefsNativeElements[index] as HTMLInputElement;
      const row = input?.closest('tr') as HTMLElement; // Get the closest row

      if (row) {
        row.classList.add('lum-c-rfq-table-form__row--highlighted');

        // Scroll smoothly to the row
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Ensure focus on the input field
        if (input) {
          input.focus();
        }

        // Remove highlight after 1 second
        setTimeout(() => {
          row.classList.remove('lum-c-rfq-table-form__row--highlighted');
        }, 1000);
      }
    }, 100);
  }

  onEnterRfq(event :Event, item :AbstractControl, index :number) :void {
    const inputValue = (event.target as HTMLInputElement).value;

    // Prevent default Enter behavior
    event.preventDefault();

    // Close the autocomplete panel
    this.autoCompleteTrigger.closePanel();
    this.enterRfqEvent.emit({
      item,
      index,
      searchStr: inputValue,
    });

    const targetIndex = Array.from(this.inputRefsNativeElements).findIndex(
      (input :Element, index :number, obj :Element[]) => {
        // Check if input id matches the itemId
        return input.id === `id-${item.value.item_id}`;
      },
    );

    this.focusToNextInput({ index: targetIndex, fromEnterEvent: true });
  }

  onSearchRfq(value :string) :void {
    this.searchRfq.emit(value);
  }

  displayFnRfq(option :any) :string {
    return option && option.sku ? `${option.product_name}[${option.sku}]` : '';
  }

  onRemoveLineItem(index :number) :void {
    this.removeLineItem.emit(index);
  }

  onAddSubtotalRow(index :number) :void {
    this.subTotalIndex = index;
    this.addRowItemSubTotal.emit(index);
  }

  onConfirmSort(column :string) :void {
    // Check if there are subtotal rows
    const subtotalRows = this.lineItemsFormArray.controls.filter((control) =>
      control.value?.row_type === ELineItemRowType.SUB_TOTAL ? control : null,
    );

    if (subtotalRows.length > 0) {
      this.dialog
        .open(UiConfirmationDialogComponent, {
          data: {
            title: 'Confirm Action',
            message:
              'Sorting will remove all subtotal rows. Do you want to continue?',
          },
          width: DialogConfig.sizes.small,
        })
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) {
            this.sortFormArray(column);
          }
        });
    } else {
      this.sortFormArray(column);
    }
  }

  sortFormArray(column :string) :void {
    // Check if there are subtotal rows
    const subtotalRows = this.lineItemsFormArray.controls.filter((control) =>
      control.value?.row_type === ELineItemRowType.SUB_TOTAL ? control : null,
    );

    const formArray = this.sortedLineItemsFormArray;

    if (this.sortState.column === column) {
      if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else if (this.sortState.direction === 'desc') {
        // Resetting to normal (original order)
        this.sortState.direction = '';
        this.sortState.column = '';
        this.resetToOriginalOrder();

        return;
      }
    } else {
      this.sortState.column = column;
      this.sortState.direction = 'asc';
    }

    const sortedFormGroups = formArray.controls.sort((a, b) => {
      let aValue = a.get(column)?.value;
      let bValue = b.get(column)?.value;

      // if sku and name is null or empty, then do not include on sorting
      if ((!a.value?.sku && !a.value?.name) || (!b.value?.sku && !b.value?.name)) {
        return 0;
      }

      switch (column) {
        case 'ordered_uom_id':
          aValue = a
            .get('unit_of_measures')
            ?.value?.find((uom :any) => uom.id === a.value?.ordered_uom_id)?.name;
          bValue = b
            .get('unit_of_measures')
            ?.value?.find((uom :any) => uom.id === b.value?.ordered_uom_id)?.name;
          break;

        case 'location_stock':
          if (!aValue) {
            aValue = ERfqStatus.UNAVAILABLE;
          }

          if (!bValue) {
            bValue = ERfqStatus.UNAVAILABLE;
          }

          const aAvailable =
            a.value?.location_stock?.qty_onhand -
            a.value?.location_stock?.qty_pending;

          const bAvailable =
            b.value?.location_stock?.qty_onhand -
            b.value?.location_stock?.qty_pending;

          const aLineQty = a.value?.quantity;
          const aLimitOos = a.value?.oss_limit_notify;

          const bLineQty = b.value?.quantity;
          const bLimitOos = b.value?.oss_limit_notify;

          if (aLimitOos > 0) {
            if (aAvailable <= aLimitOos || aAvailable - aLineQty <= aLimitOos) {
              aValue = ERfqStatus.UNAVAILABLE;
            }
          } else {
            if (aAvailable <= 0 || aLineQty >= aAvailable) {
              aValue = ERfqStatus.UNAVAILABLE;
            } else {
              aValue = ERfqStatus.AVAILABLE;
            }
          }

          if (bLimitOos > 0) {
            if (bAvailable <= bLimitOos || bAvailable - bLineQty <= bLimitOos) {
              bValue = ERfqStatus.UNAVAILABLE;
            }
          } else {
            if (bAvailable <= 0 || bLineQty >= bAvailable) {
              bValue = ERfqStatus.UNAVAILABLE;
            } else {
              bValue = ERfqStatus.AVAILABLE;
            }
          }

          break;

        case 'category':
          aValue = aValue?.product_category;
          bValue = bValue?.product_category;
          break;

        case 'subcategory':
          aValue = aValue?.sub_category;
          bValue = bValue?.sub_category;
          break;

        default:
          break;
      }

      // Handle null and ensure string comparison
      const aString = !aValue
        ? ''
        : typeof aValue === 'string'
          ? aValue.toString().toLowerCase()
          : aValue;
      const bString = !bValue
        ? ''
        : typeof bValue === 'string'
          ? bValue.toString().toLowerCase()
          : bValue;

      if (aString < bString) {
        return this.sortState.direction === 'asc' ? -1 : 1;
      } else if (aString > bString) {
        return this.sortState.direction === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });

    // Reassign sorted form groups to the FormArray
    for (let i = 0; i < sortedFormGroups.length; i++) {
      formArray.setControl(i, sortedFormGroups[i]);
    }

    this.subTotalIndex = null;
    this.addRowItemSubTotal.emit(null);
  }

  private resetToOriginalOrder() :void {
    this.lineItemsFormArray.controls.forEach((control, index) => {
      this.sortedLineItemsFormArray.setControl(index, control);
    });
  }

  private listentToDOMNodeInsertedEvent() :void {
    // Attach event listener for new rows added to the table
    const table = document.querySelector('.lum-table') as HTMLTableElement;
    table.addEventListener('DOMNodeInserted', () => {
      this.setInputsNodeList();
    });
  }

  private setInputsNodeList() :void {
    const inputs = document.querySelectorAll(this.querySelectorStr);
    this.inputsNodeList = inputs;
  }

  private focusToNextInput(data :{
    index :number;
    fromEnterEvent ?:boolean;
  }) :void {
    const { index, fromEnterEvent } = data;
    let nextIndex = index + 1;

    if (this.inputRefsNativeElements.length === nextIndex) {
      this.addRowItems.emit(1);

      setTimeout(() => {
        const nextInput = this.inputRefsNativeElements[
          fromEnterEvent ? nextIndex : nextIndex - 1
        ] as HTMLInputElement;
        nextInput.focus();
      }, 500);
    } else {
      while (nextIndex < this.inputRefsNativeElements?.length) {
        const nextInput = this.inputRefsNativeElements[
          nextIndex
        ] as HTMLInputElement;
        if (
          !nextInput.disabled &&
          !nextInput.readOnly &&
          nextInput.classList.contains('table__search-skus-input')
        ) {
          setTimeout(() => {
            nextInput.focus();
          }, 500);

          return;
        }
        nextIndex++;
      }
    }
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('line_items') as UntypedFormArray;
  }

  get sortedLineItemsFormArray() :UntypedFormArray {
    return this.duplicateForm?.get('line_items') as UntypedFormArray;
  }

  get isOverride() :boolean {
    return this.form.get('overridden')?.value || 0;
  }

  get inputRefsNativeElements() :HTMLInputElement[] {
    return this.inputRefs.toArray().map((input) => input.nativeElement);
  }

  get displayColumns() :{
    [key :string] :{ visible :boolean; label :string };
  } {
    return this.columns.reduce(
      (acc, { id, visible, label }) => {
        acc[id] = { visible, label };

        return acc;
      },
      {} as { [key :string] :{ visible :boolean; label :string } },
    );
  }

  get isInvoiceStatusCompleteSalesOrderStatusShipped() :boolean {
    return this.isInvoiceStatusComplete || this.isSalesOrderShippedStatus;
  }
}
